import { Add, ArrowBack, Close, DeleteForever, Save, Search } from '@mui/icons-material';
import {
  Alert,
  Button as SMButton,
  ButtonGroup,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { deepEqual } from 'fast-equals';
import { Field, FieldArray, Form, Formik } from 'formik';
import { useSnackbar } from 'notistack';
import { UserContext } from '../../../context';
import { FC, Fragment, useContext, useEffect, useMemo, useState } from 'react';
import { Prompt, useHistory, useParams } from 'react-router-dom';
import * as Yup from 'yup';
// Components
import {
  Button,
  EditableCell,
  Loader,
  OrderManagementPage,
  PageTitle,
  Table,
  ImageModal,
  Link
} from '../../../components';
import {
  getNeighborhoodVendors,
  getOrderDetail,
  getPlaceOrderDescriptions,
  getPlaceOrderStatuses,
  getPlaceOrderTypes,
  getReturnParts,
  getUndefinedParts,
  updateOrderDetail,
  updateOrderStatus
} from '../../../fetch';
import { formatMoney, UNDEFINED_PART, decimalRegExp } from '../../../helpers';
import {
  INeighborhood,
  INeighborhoodVendor,
  IOrderDetail,
  IOrderDetailPart,
  IOrderStatus,
  IOrderType,
  IPart,
  IReturnPartPO,
  IReturnPOPart,
  IUndefinedPart,
  ILot
} from '../../../models';
import { FindPartsModal } from '../place-order/find-parts-modal';
import { PlaceOrderImages } from '../place-order/PlaceOrderImages';
import { MobileParts } from './mobile-parts';
import { OrderHistory } from './OrderHistory';
import { PartNameRow } from './part-name-row';
import { PriceRow } from './price-row';
import { StatusHistoryModal } from './status-history-modal';
import { VendorRow } from './vendor-row';
import { getCommonNeighborhoodLots, getProductionNeighborhoods } from '../../../fetch/lookups';

interface IOrderManagementDetail {
  orderId?: string;
  isModal?: boolean;
  isPartsHistory?: boolean;
}
const EditOrderSchema = Yup.object().shape({
  fieldOrderId: Yup.string(),
  neighborhoodId: Yup.string(),
  lotNumber: Yup.string().required(),
  orderType: Yup.mixed().when('status', {
    is: (val: string) => val !== 'Cancelled',
    then: Yup.string().required('Required'),
    otherwise: Yup.string().notRequired()
  }),
  chargeVendors: Yup.mixed().when('orderType', {
    is: (val: number) => val === 6 || val === 10,
    then: Yup.array(
      Yup.object().shape({
        vendorId: Yup.string().required('Required'),
        amount: Yup.string().required('Required')
      })
    ),
    otherwise: Yup.array().notRequired()
  }),
  notes: Yup.string().required('Required'),
  descriptionId: Yup.mixed().when('status', {
    is: (val: string) => val !== 'Cancelled',
    then: Yup.string().required('Required'),
    otherwise: Yup.string().notRequired()
  }),
  comments: Yup.mixed().when('status', {
    is: (val: string) => val === 'Remediating',
    then: Yup.string().required('Required'),
    otherwise: Yup.string().notRequired()
  }),
  status: Yup.string(),
  photoGuids: Yup.array(),
  photos: Yup.mixed().when('orderType', {
    // required if the orderType is a Return PO
    is: (val: string) => val === '9',
    then: Yup.array().required('Required'),
    otherwise: Yup.array().notRequired()
  })
});

export const OrderManagementDetail: FC<IOrderManagementDetail> = ({
  orderId,
  isModal,
  isPartsHistory
}) => {
  const { fieldOrderId: paramsOrderId }: { fieldOrderId: string } = useParams();
  const fieldOrderId = orderId || paramsOrderId;
  const history = useHistory();
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [currentOrder, setCurrentOrder] = useState<IOrderDetail | null>(null);
  const [lots, setLots] = useState<ILot[] | null>(null);
  const [parts, setParts] = useState<IOrderDetailPart[]>([]);
  const [neighborhoods, setNeighborhoods] = useState<INeighborhood[]>(null);
  const [selectedNeighborhoodId, setSelectedNeighborhoodId] = useState<number | null>(null);
  const [selectedLotId, setSelectedLotId] = useState<number | string | null>(null);
  const [isLoadingNeighborhoods, setNeighborhoodsLoading] = useState<boolean>(true);
  const [isLoadingNeighborhoodLots, setNeighborhoodsLotsLoading] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(true);
  // used to hold the selected value outside the form
  const [selectedOrderType, setSelectedOrderType] = useState<number | null>(null);
  const [orderTypes, setOrderTypes] = useState<IOrderType[] | null>(null);
  const [isLoadingTypes, setLoadingTypes] = useState<boolean>(false);
  const [isLoadingStatuses, setLoadingStatuses] = useState<boolean>(false);
  const [selectedImage, setSelectedImage] = useState<string>('');
  const [statuses, setStatuses] = useState<IOrderStatus[] | null>(null);
  const [showFindPartsModal, setShowFindPartsModal] = useState<boolean>(false);
  const [isShowingStatusHistory, showStatusHistory] = useState<boolean>(false);
  const [isLoadingVendors, setIsLoadingVendors] = useState<boolean>(false);
  const [availableVendors, setAvailableVendors] = useState<INeighborhoodVendor[] | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string>('');

  const { isWarrantyAdmin } = useContext(UserContext);

  const checkVendors = () => {
    const allVendorsHavePOManager = parts
      .flatMap(part => validationVendors.filter(vendor => part.vendorId === vendor.vendorId))
      .every(vendor => vendor.hasPoManager);
    return !allVendorsHavePOManager;
  };
  const [validationVendors, setValidationVendors] = useState([]);
  const isMobile = useMediaQuery('(max-width: 960px)');

  const fetchOrderStatuses = async () => {
    setLoadingStatuses(true);
    try {
      const res = await getPlaceOrderStatuses();
      setStatuses(res);
    } catch (error) {
      console.error(error);
    }
    setLoadingStatuses(false);
  };

  const fetchOrderTypes = async () => {
    setLoadingTypes(true);
    try {
      const res = await getPlaceOrderTypes({
        isActive: true
      });
      setOrderTypes(res);
    } catch (error) {
      console.error(error);
    }
    setLoadingTypes(false);
  };

  const [loadingDescriptions, setLoadingDescriptions] = useState(false);
  const [descriptions, setDescriptions] = useState(null);
  const fetchDescriptionsTypes = async () => {
    setLoadingDescriptions(true);
    try {
      const res = await getPlaceOrderDescriptions({
        isActive: true
      });
      setDescriptions(res);
    } catch (error) {
      console.error(error);
    }
    setLoadingDescriptions(false);
  };

  const fetchVendors = async (neighborhoodId: number | string, lotId: string | number) => {
    setIsLoadingVendors(true);
    try {
      const res = await getNeighborhoodVendors(neighborhoodId, {
        // if less than zero we know the user selected "General Job" from the lots dropdown
        lotNumber: lotId,
        isReturnPO: selectedOrderType === 9
      });
      setAvailableVendors(res.records);
    } catch (error) {
      console.error(error);
    }

    setIsLoadingVendors(false);
  };

  const fetchNeighborhoodLots = async () => {
    setNeighborhoodsLotsLoading(true);
    try {
      const res = await getCommonNeighborhoodLots({
        neighborhoodId: selectedNeighborhoodId,
        // this has to be true for the BE to determine whether the General Job option should be included in the dropdown
        includeGeneralJob: true
      });
      setLots(res);
    } catch (error) {
      console.error(error);
    }
    setNeighborhoodsLotsLoading(false);
  };

  const fetchOrderDetail = async () => {
    try {
      setLoading(true);
      const res = await getOrderDetail(fieldOrderId, isPartsHistory);
      if (res?.currentStatus) {
        const updatedParts = res.parts.map(part => ({
          ...part,
          partId: part.partId ?? null,
          undefinedPartDescriptionId: part.undefinedPartDescriptionId ?? null,
          purchaseOrderNumber: part.returnForPurchaseOrderNumber
        }));
        setCurrentOrder({
          ...res,
          parts: updatedParts
        });
        setParts(updatedParts);
        setSelectedNeighborhoodId(res.neighborhoodId);
        setSelectedOrderType(res.type);
        setSelectedStatus(res?.currentStatus?.status ?? '');
      } else {
        setLoading(false);
        enqueueSnackbar(`Error loading order, redirecting...`, {
          variant: 'error'
        });
        // set minor timeout so the redirecting doesn't go too fast
        setTimeout(() => {
          history.push('/production/orders/order-management');
        }, 1000);
        console.error(`No 'records' on fetchOrderDetail response.`);
      }
    } catch (error) {
      setLoading(false);
      enqueueSnackbar(`Error loading order, redirecting...`, {
        variant: 'error'
      });
      // set minor timeout so the redirecting doesn't go too fast
      setTimeout(() => {
        history.push('/production/orders/order-management');
      }, 1000);
      console.error(error);
    }
    setLoading(false);
  };

  const fetchNeighborhoods = async () => {
    try {
      const res = await getProductionNeighborhoods({
        isActive: isWarrantyAdmin ? null : true,
        hasActiveJobs: false,
        includeGeneralJob: true
      });
      setNeighborhoods(res.records);
    } catch (error) {
      console.error(error);
    }
    setNeighborhoodsLoading(false);
  };

  const checkParts = () => {
    const undefinedParts = parts.filter(
      part => part.partName === UNDEFINED_PART || !!part.undefinedPartDescriptionId
    );
    const invalidParts = undefinedParts.filter(
      part =>
        !part.undefinedPartDescriptionId || (currentOrder?.isChangeOrder && !part.pricePerUnit)
    );
    if (invalidParts.length > 0) {
      return true;
    }
    return false;
  };

  const [returnParts, setReturnParts] = useState<IReturnPartPO[]>([]);
  const [isLoadingReturnParts, setLoadingReturnParts] = useState<boolean>(false);
  const fetchReturnParts = async (
    vendorId: number,
    fieldOrderId: number,
    neighborhoodId: number,
    lotNumber: number
  ) => {
    setLoadingReturnParts(true);
    try {
      const res = await getReturnParts({
        vendorId: vendorId,
        fieldOrderId: fieldOrderId,
        neighborhoodId: neighborhoodId,
        lotNumber: lotNumber
      });
      setReturnParts(res);
    } catch (error) {
      enqueueSnackbar(`Error loading parts, please try again.`, {
        variant: 'error'
      });
      console.log(error);
    } finally {
      setLoadingReturnParts(false);
    }
  };

  useEffect(() => {
    fetchOrderStatuses();
    fetchNeighborhoods();
    fetchOrderDetail();
    fetchOrderTypes();
    fetchDescriptionsTypes();
    fetchUndefinedPartOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldOrderId]);

  useEffect(() => {
    if ((currentOrder && currentOrder?.type === 9) || selectedOrderType === 9) {
      // Vendor Id: Since a PO can now only be placed per vendor, we're just going to take the first part's vendorId
      const vendorId = currentOrder.parts[0].vendorId;
      fetchReturnParts(
        vendorId,
        currentOrder.fieldOrderId,
        currentOrder.neighborhoodId,
        currentOrder.lotNumber
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrder?.type, selectedOrderType]);

  useEffect(() => {
    if (currentOrder) {
      fetchVendors(currentOrder.neighborhoodId, currentOrder.lotNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrder, selectedOrderType]);

  useEffect(() => {
    if (selectedNeighborhoodId) {
      fetchNeighborhoodLots();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNeighborhoodId]);

  const partsChanged = !deepEqual(parts, currentOrder && currentOrder.parts);
  const totalCost = parts.reduce<number>(
    (acc, curr) => acc + curr.pricePerUnit * Number(curr.quantity),
    0
  );
  // the order is editable in these statuses
  const canEdit =
    currentOrder?.currentStatus?.status === 'New' ||
    currentOrder?.currentStatus?.status === 'Remediating' ||
    currentOrder?.currentStatus?.status === 'Approved';
  // if loading, wrong status, preview modal or the order type is already set to open ended po
  // don't allow them to change the order type select
  const isOrderTypeDisabled = isLoadingTypes || !canEdit || isModal || currentOrder?.type === 20;

  const updateMyPartsData = (
    part: IOrderDetailPart,
    columnId: string,
    value: string,
    error?: boolean
  ) => {
    setParts(old => {
      return old.map(row => {
        if (row.partId === part.partId) {
          return {
            ...part,
            [columnId]: value
          };
        }
        return row;
      });
    });
  };

  const [isLoadingUndefinedParts, setIsLoadingUndefinedParts] = useState<boolean>(false);
  const [undefinedParts, setUndefinedParts] = useState<IUndefinedPart[]>([]);
  const fetchUndefinedPartOptions = async (callback?: any) => {
    try {
      setIsLoadingUndefinedParts(true);
      const res = await getUndefinedParts({
        sortBy: 'description',
        sortDirection: 'Asc',
        showOnlyUsedInOrders: false
      });
      setUndefinedParts(res.records);
    } catch (error) {
      enqueueSnackbar(`Error loading undefined parts, please try again.`, {
        variant: 'error'
      });
      console.log(error);
    } finally {
      setIsLoadingUndefinedParts(false);
      if (callback) {
        callback();
      }
    }
  };
  const columns = useMemo(
    () => [
      {
        Header: '#',
        accessor: 'partId',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IOrderDetailPart } };
        }) => {
          return (
            <Typography>
              {original.partName === UNDEFINED_PART || !!original.undefinedPartDescriptionId
                ? '--'
                : original.partId}
            </Typography>
          );
        }
      },
      {
        Header: 'Part',
        accessor: 'partName',
        sort: false,
        Cell: ({ cell: { row } }: { cell: { row } }) => {
          const part: IOrderDetailPart = row.original;
          return part.partName === UNDEFINED_PART || !!part.undefinedPartDescriptionId ? (
            <PartNameRow
              id={part.partId}
              part={part}
              handlePartNameChange={(undefinedPartDescriptionId: number) => {
                const copy = [...parts];
                copy[row.index] = {
                  ...copy[row.index],
                  undefinedPartDescriptionId
                };
                setParts(copy);
              }}
              isLoading={isLoadingUndefinedParts}
              undefinedParts={undefinedParts}
              updateOptions={callback => {
                fetchUndefinedPartOptions(callback);
              }}
            />
          ) : (
            <Typography>{part.partName}</Typography>
          );
        }
      },
      {
        Header: 'Qty.',
        accessor: 'quantity',
        sort: false,
        overrideWidth: 150,
        Cell: props => {
          return props.cell.row.original.hasPartPermission &&
            props.cell.row.original.partName !== UNDEFINED_PART &&
            !props.cell.row.original.undefinedPartDescriptionId ? (
            currentOrder?.type === 9 ? (
              <EditableCell
                disabled={isModal}
                {...props}
                isNumberOnly
                props
                endAdornment={props.cell.row.original.unit}
                value={
                  +props.cell?.value > props.cell.row.original?.quantityRemaining
                    ? props.cell.row.original?.quantityRemaining
                    : props?.value
                }
              />
            ) : (
              <EditableCell
                {...props}
                isNumberOnly
                disabled={isModal}
                endAdornment={props.cell.row.original.unit}
              />
            )
          ) : (
            props.cell.row.original.quantity
          );
        }
      },
      {
        Header: 'Vendor',
        accessor: 'vendorName',
        overrideWidth: 400,
        sort: false,
        Cell: ({ cell: { row } }: { cell: { row } }) => {
          const part: IOrderDetailPart = row.original;
          return part.partName !== UNDEFINED_PART && !part.undefinedPartDescriptionId ? (
            <VendorRow
              showError
              isRequired={selectedStatus === 'Cancelled' ? false : !isModal}
              isDisabled={!canEdit || isModal || !row.original.hasPartPermission}
              setValidationVendors={setValidationVendors}
              part={row.original}
              handleVendorChange={(vendorId: number, pricePerUnit: number) => {
                const copy = [...parts];
                copy[row.index] = {
                  ...copy[row.index],
                  vendorId,
                  pricePerUnit
                };
                setParts(copy);
              }}
            />
          ) : (
            <></>
          );
        }
      },
      {
        Header: 'Price',
        accessor: 'pricePerUnit',
        sort: false,
        overrideWidth: 125,
        Cell: ({ cell: { row } }: { cell: { row } }) => {
          const part: IOrderDetailPart = row.original;
          if (
            (part.partName === UNDEFINED_PART || !!part.undefinedPartDescriptionId) &&
            currentOrder?.isChangeOrder
          ) {
            return (
              <PriceRow
                handlePriceChange={(pricePerUnit: number | undefined) => {
                  const copy = [...parts];
                  if (pricePerUnit) {
                    copy[row.index] = {
                      ...copy[row.index],
                      pricePerUnit: pricePerUnit
                    };
                  } else {
                    delete copy[row.index].pricePerUnit;
                  }
                  setParts(copy);
                }}
                isLoading={isLoadingUndefinedParts}
                value={part.pricePerUnit}
              />
            );
          } else if (
            (part.partName !== UNDEFINED_PART && !part.undefinedPartDescriptionId) ||
            ((part.partName === UNDEFINED_PART || !!part.undefinedPartDescriptionId) &&
              part.pricePerUnit)
          ) {
            return <>{`${selectedOrderType === 9 ? '-' : ''}${formatMoney(part.pricePerUnit)}`}</>;
          } else {
            return <>--</>;
          }
        }
      },
      {
        id: 'actions',
        accessor: '',
        Header: '',
        overrideWidth: 100,
        sort: false,
        Cell: ({ cell: { row } }: { cell: { row } }) => {
          const part: IOrderDetailPart = row.original;
          return (
            !isModal && (
              <div className={classes.Vendor}>
                <IconButton
                  color="default"
                  disabled={!canEdit || !part.hasPartPermission}
                  className={classes.deleteButton}
                  title="Remove part from cart"
                  onClick={() => {
                    const result = window.confirm(
                      'Are you sure you want to remove this part from your order?'
                    );
                    if (result) {
                      setParts(parts.filter((p, index) => index !== row.index));
                    }
                  }}
                  size="large"
                >
                  <DeleteForever />
                </IconButton>
              </div>
            )
          );
        }
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      parts,
      selectedOrderType,
      selectedStatus,
      selectedNeighborhoodId,
      selectedLotId,
      undefinedParts,
      isLoadingUndefinedParts
    ]
  );
  const hasUndefinedParts =
    parts.filter(part => part.partName === UNDEFINED_PART || !!part.undefinedPartDescriptionId)
      .length > 0;
  return (
    <div key={fieldOrderId}>
      <Formik
        enableReinitialize={true}
        initialValues={{
          fieldOrderId: currentOrder?.fieldOrderId ?? '',
          neighborhoodId: currentOrder?.neighborhoodId.toString() ?? '',
          lotNumber: currentOrder?.lotNumber?.toString() ?? '',
          orderType: currentOrder?.type ?? '',
          chargeVendors:
            currentOrder?.chargeVendors?.map(vendor => ({
              ...vendor,
              amount: vendor.amount === null ? '' : formatMoney(vendor.amount)
            })) ?? [],
          descriptionId: currentOrder?.descriptionId ?? '',
          notes: currentOrder?.notes ?? '',
          changeOrder: currentOrder?.isChangeOrder ?? false,
          comments: currentOrder?.currentStatus?.comment ?? '',
          status: currentOrder?.currentStatus?.status ?? '',
          photos: currentOrder?.photos ?? [],
          photoGuids: currentOrder?.photos?.map(p => p.photoGuid)
        }}
        validationSchema={EditOrderSchema}
        onSubmit={async values => {
          const showError = res => {
            if (res && res.Detail) {
              return enqueueSnackbar(`Error, ${res.Detail}`, {
                variant: 'error'
              });
            } else if (
              res &&
              res.Errors &&
              Object.values(res.Errors)[0] &&
              Object.values(Object.values(res.Errors)[0])[0]
            ) {
              return enqueueSnackbar(`Error, ${Object.values(Object.values(res.Errors)[0])[0]}`, {
                variant: 'error'
              });
            }
          };
          try {
            // only call this section if the status is cancelled
            if (values.status === 'Cancelled') {
              const statusRes = await updateOrderStatus({
                fieldOrderId: currentOrder.fieldOrderId,
                status: values.status,
                comment: values.comments
              });
              showError(statusRes);
              enqueueSnackbar(`Order Updated!`, {
                variant: 'success'
              });
              return await fetchOrderDetail();
            }
            const orderRes = await updateOrderDetail({
              fieldOrderId: currentOrder.fieldOrderId,
              isChangeOrder: currentOrder.isChangeOrder,
              chargeVendors: values.chargeVendors.map(vendor => {
                return {
                  vendorId: vendor.vendorId,
                  amount: vendor.amount ? Number(vendor.amount.replace(/[^0-9.]+/g, '')) : null
                };
              }),
              neighborhoodId: Number(values.neighborhoodId),
              lotNumber: Number(values.lotNumber),
              type: Number(values.orderType),
              descriptionId: Number(values.descriptionId),
              notes: values.notes,
              parts: parts.map(part => ({
                fieldOrderPartId: part.fieldOrderPartId ?? null,
                partId: part.partName === UNDEFINED_PART ? null : part.partId || part.id,
                undefinedPartDescriptionId: part.undefinedPartDescriptionId ?? null,
                price: part.pricePerUnit ?? null,
                quantity: part.quantity,
                vendorId: part.vendorId,
                purchaseOrderNumber: part.purchaseOrderNumber ?? null
              })),
              // send along valid guid strings
              photoGuids: values.photoGuids.filter(Boolean),
              unapprovedOrderAlert: false
            });
            if (orderRes && orderRes.Detail) {
              return enqueueSnackbar(`Error, ${orderRes.Detail}`, {
                variant: 'error'
              });
            } else if (
              orderRes &&
              orderRes.Errors &&
              Object.values(orderRes.Errors)[0] &&
              Object.values(Object.values(orderRes.Errors)[0])[0]
            ) {
              return enqueueSnackbar(
                `Error, ${Object.values(Object.values(orderRes.Errors)[0])[0]}`,
                {
                  variant: 'error'
                }
              );
            }
            // only call this endpoint if the status changed or they added/updated the comments textbox
            if (
              values.status !== currentOrder.currentStatus.status ||
              (values.comments && values.comments !== currentOrder.currentStatus.comment)
            ) {
              const statusRes = await updateOrderStatus({
                fieldOrderId: currentOrder.fieldOrderId,
                status: values.status,
                comment: values.comments
              });
              if (statusRes && statusRes.Detail) {
                return enqueueSnackbar(`Error, ${statusRes.Detail}`, {
                  variant: 'error'
                });
              } else if (
                statusRes &&
                statusRes.Errors &&
                Object.values(statusRes.Errors)[0] &&
                Object.values(Object.values(statusRes.Errors)[0])[0]
              ) {
                return enqueueSnackbar(
                  `Error, ${Object.values(Object.values(statusRes.Errors)[0])[0]}`,
                  {
                    variant: 'error'
                  }
                );
              }
            }
            await fetchOrderDetail();
            enqueueSnackbar(`Order Updated!`, {
              variant: 'success'
            });
          } catch (error) {
            console.log(error);
            enqueueSnackbar('Sorry, failed to update order. Please try again.', {
              variant: 'success'
            });
          }
        }}
      >
        {({
          dirty,
          values,
          setFieldValue,
          errors,
          touched,
          handleBlur,
          isSubmitting,
          isValid,
          setFieldTouched
        }) => {
          const isReturnPOOrderType = values.orderType === 9;
          const checkImages = () => {
            // Check if there are images and prevent submission if required for specific instances.
            if (!values.photoGuids.length && isReturnPOOrderType) {
              return true;
            } else {
              return false;
            }
          };
          return (
            <Form>
              <Prompt
                when={currentOrder ? dirty || partsChanged : false}
                message="Are you sure you want to navigate away from this page? Your changes will be lost."
              />
              <OrderManagementPage
                title={`${!isModal && canEdit ? 'Edit' : ''} Order${
                  currentOrder ? ` - ${currentOrder.fieldOrderId}` : ''
                }`}
                isModal={isModal}
              >
                {(isLoading || isSubmitting) && <Loader position="centered" type="fullscreen" />}
                {!isLoading && currentOrder && (
                  <>
                    <Grid container className={classes.titleWrapper}>
                      {!isModal && (
                        <Grid item>
                          <IconButton
                            title={`Back to Order Management`}
                            onClick={() => history.push('/production/orders/order-management')}
                            size="large"
                          >
                            <ArrowBack />
                          </IconButton>
                        </Grid>
                      )}
                      <Grid item xs={12} className={classes.pageTitleWrapper}>
                        <PageTitle
                          showDivider={false}
                          title={`${!isModal ? 'Edit ' : ' '}BLinq${
                            currentOrder ? ` No.${currentOrder.fieldOrderId}` : ''
                          }`}
                        />
                      </Grid>
                      <Grid item className={classes.infoWrapper}>
                        <Typography component="span" variant="h6" color="secondary">
                          Owner:
                        </Typography>{' '}
                        <Typography component="span" variant="h6">
                          {currentOrder.orderPlacer}
                        </Typography>
                        <div>
                          {currentOrder.sageOrderNumbers.length > 0 ? (
                            <>
                              <Typography component="span" variant="h6" color="secondary">
                                Order Numbers:
                              </Typography>{' '}
                              <Typography component="span" variant="h6">
                                {currentOrder.sageOrderNumbers.join(', ')}
                              </Typography>
                            </>
                          ) : null}
                        </div>
                        <div>
                          <Typography component="span" variant="h6" color="secondary">
                            Total Cost:
                          </Typography>{' '}
                          <Typography component="span" variant="h6">
                            {formatMoney(totalCost)}
                          </Typography>
                        </div>
                      </Grid>
                    </Grid>
                    <Divider />
                    {hasUndefinedParts && !isModal && (
                      <Alert severity="warning">
                        This order contains undefined parts. You won't be able to place or approve
                        this order until those parts are removed or replaced with valid parts.
                      </Alert>
                    )}
                    {currentOrder?.isBackcharge && (
                      <Alert severity="warning">
                        Must enter a partial chargeback amount, or the full amount of the cart will
                        be charged to the vendor(s).
                      </Alert>
                    )}
                    {currentOrder?.potentialDuplicateOrderIds.length > 0 && (
                      <Alert severity="warning">
                        This order could be a duplicate of the following order(s):{' '}
                        {currentOrder.potentialDuplicateOrderIds.map((duplicateId, index) => {
                          return (
                            <>
                              <Link key={`${index}`} to={`${duplicateId}`}>
                                BLinq No. {duplicateId}
                              </Link>
                              {index + 1 !== currentOrder.potentialDuplicateOrderIds.length
                                ? ', '
                                : ''}
                            </>
                          );
                        })}
                      </Alert>
                    )}
                    {/* md = 2.4 is 12 / 5. It makes 5 equal columns */}
                    <Grid container spacing={1} className={classes.marginTop}>
                      <Grid item xs={12} sm={6} md={2.4}>
                        <Field name={`status`}>
                          {({ field }) => {
                            return (
                              <>
                                <FormControl fullWidth variant="outlined" size="small">
                                  <InputLabel id="status" className={classes.outlinedLabel}>
                                    Status
                                  </InputLabel>
                                  <Select
                                    classes={{
                                      select:
                                        values.status === 'Remediating' ? classes.statusRed : null
                                    }}
                                    disabled={isLoadingStatuses || !canEdit || isModal}
                                    name="status"
                                    labelId="status"
                                    id="status"
                                    value={values.status}
                                    onChange={e => {
                                      if (e.target.value === 'Remediating' && !values.comments) {
                                        setFieldTouched('comments', true);
                                      }
                                      setSelectedStatus(e.target.value);
                                      field.onChange(e);
                                    }}
                                  >
                                    {statuses &&
                                      statuses.map((status, index) => {
                                        return (
                                          <MenuItem
                                            key={`${index}`}
                                            value={status.value}
                                            disabled={
                                              hasUndefinedParts &&
                                              (status.description === 'Placed' ||
                                                status.description === 'Approved')
                                                ? true
                                                : false
                                            }
                                          >
                                            {status.description}
                                          </MenuItem>
                                        );
                                      })}
                                  </Select>
                                </FormControl>
                                {!isModal && (
                                  <Button
                                    color="secondary"
                                    id="view-status-history"
                                    onClick={() => showStatusHistory(true)}
                                  >
                                    View Status History
                                  </Button>
                                )}
                              </>
                            );
                          }}
                        </Field>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2.4}>
                        <Field name={`neighborhoodId`}>
                          {({ field }) => {
                            return (
                              <FormControl fullWidth variant="outlined" size="small">
                                <InputLabel id={`neighborhoodId`} className={classes.outlinedLabel}>
                                  Neighborhood
                                </InputLabel>
                                <Select
                                  {...field}
                                  onChange={e => {
                                    field.onChange(e);
                                    setFieldValue('lotNumber', '');
                                    setSelectedNeighborhoodId(e.target.value as number);
                                  }}
                                  name={`neighborhoodId`}
                                  labelId={`neighborhoodId`}
                                  id={`neighborhoodId`}
                                  size="small"
                                  value={values.neighborhoodId}
                                  onBlur={e => handleBlur(e)}
                                  disabled={
                                    isLoadingNeighborhoods ||
                                    !canEdit ||
                                    isModal ||
                                    isReturnPOOrderType
                                  }
                                  error={errors && errors.neighborhoodId ? true : false}
                                >
                                  {neighborhoods &&
                                    neighborhoods.map((neighborhood: INeighborhood, index) => {
                                      return (
                                        <MenuItem
                                          key={`${index}`}
                                          value={neighborhood.neighborhoodId}
                                        >
                                          {neighborhood.name} Phase {neighborhood.phase}
                                        </MenuItem>
                                      );
                                    })}
                                </Select>
                              </FormControl>
                            );
                          }}
                        </Field>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2.4}>
                        <Field name={`lotNumber`}>
                          {({ field }) => {
                            return (
                              <FormControl required fullWidth variant="outlined" size="small">
                                <InputLabel id={`lotNumber`} className={classes.outlinedLabel}>
                                  {isLoadingNeighborhoodLots ? 'Loading...' : 'Lot'}
                                </InputLabel>
                                <Select
                                  {...field}
                                  onChange={e => {
                                    field.onChange(e);
                                    setSelectedLotId(e.target.value as string);
                                    fetchVendors(values.neighborhoodId, e.target.value as string);
                                  }}
                                  name={`lotNumber`}
                                  labelId={`lotNumber`}
                                  id={`lotNumber`}
                                  size="small"
                                  value={values.lotNumber}
                                  onBlur={handleBlur}
                                  disabled={
                                    isLoadingNeighborhoodLots ||
                                    !canEdit ||
                                    isModal ||
                                    !values.neighborhoodId ||
                                    isReturnPOOrderType
                                  }
                                  error={errors && errors.lotNumber ? true : false}
                                >
                                  {!isLoadingNeighborhoodLots && lots?.length === 0 && (
                                    <MenuItem disabled>No Lots available</MenuItem>
                                  )}
                                  {lots &&
                                    lots.map((lot: ILot, index) => {
                                      return (
                                        <MenuItem key={`${index}`} value={lot.lotNumber}>
                                          {lot.description}
                                        </MenuItem>
                                      );
                                    })}
                                </Select>
                              </FormControl>
                            );
                          }}
                        </Field>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2.4}>
                        <Field name={`orderType`}>
                          {({ field }) => {
                            return (
                              <FormControl
                                required={values.status !== 'Cancelled'}
                                fullWidth
                                variant="outlined"
                                size="small"
                              >
                                <InputLabel id={`orderType`} className={classes.outlinedLabel}>
                                  Order Type
                                </InputLabel>
                                <Select
                                  {...field}
                                  name={`orderType`}
                                  labelId={`orderType`}
                                  id={`orderType`}
                                  size="small"
                                  value={values.orderType}
                                  onBlur={handleBlur}
                                  disabled={isOrderTypeDisabled || isReturnPOOrderType}
                                  error={
                                    errors && errors.orderType && touched && touched.orderType
                                      ? true
                                      : false
                                  }
                                  onChange={e => {
                                    // order type is either "PRODUCTION BACKCHARGE" or "WARRANTY BACKCHARGE"
                                    if (values.orderType === 10 || values.orderType === 6) {
                                      setFieldValue('descriptionId', '100');
                                    }
                                    // order type is changed from "PRODUCTION BACKCHARGE"/"WARRANTY BACKCHARGE" to a different type
                                    // and it was originally a "PRODUCTION BACKCHARGE" or "WARRANTY BACKCHARGE"
                                    // then reset charge vendors to empty
                                    if (
                                      Number(e.target.value) !== 10 &&
                                      Number(e.target.value) !== 6 &&
                                      currentOrder?.chargeVendors?.length
                                    ) {
                                      setFieldValue('chargeVendors', []);
                                    }
                                    // set state so we can have access to this field outside the form
                                    setSelectedOrderType(Number(e.target.value));
                                    field.onChange(e);
                                  }}
                                >
                                  {orderTypes &&
                                    //Filter out "Return PO" if it did not come to this page already set as one
                                    orderTypes
                                      .filter(type => {
                                        if (!isReturnPOOrderType) {
                                          return type.sageId !== 9;
                                        }
                                        return true;
                                      })
                                      .map((type, index) => {
                                        return (
                                          <MenuItem key={`${index}`} value={type.sageId}>
                                            {type.typeName}
                                          </MenuItem>
                                        );
                                      })}
                                </Select>
                              </FormControl>
                            );
                          }}
                        </Field>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2.4}>
                        <Field name={`descriptionId`}>
                          {({ field }) => {
                            return (
                              <FormControl
                                required={values.status !== 'Cancelled'}
                                fullWidth
                                variant="outlined"
                                size="small"
                              >
                                <InputLabel id={`descriptionId`} className={classes.outlinedLabel}>
                                  Order Desc.
                                </InputLabel>
                                <Select
                                  {...field}
                                  name={`descriptionId`}
                                  labelId={`descriptionId`}
                                  id={`descriptionId`}
                                  size="small"
                                  value={
                                    values.orderType === 10 || values.orderType === 6
                                      ? '100'
                                      : values.descriptionId
                                  }
                                  onBlur={handleBlur}
                                  disabled={
                                    loadingDescriptions ||
                                    !canEdit ||
                                    isModal ||
                                    values.orderType === 10 ||
                                    values.orderType === 6 ||
                                    isReturnPOOrderType
                                  }
                                  error={
                                    errors &&
                                    errors.descriptionId &&
                                    touched &&
                                    touched.descriptionId
                                      ? true
                                      : false
                                  }
                                >
                                  {descriptions &&
                                    descriptions.map((description, index) => {
                                      return (
                                        <MenuItem key={`${index}`} value={description.id}>
                                          {description.name}
                                        </MenuItem>
                                      );
                                    })}
                                </Select>
                              </FormControl>
                            );
                          }}
                        </Field>
                      </Grid>
                      <Grid item xs={12}>
                        <Field name={`notes`}>
                          {({ field }) => {
                            return (
                              <TextField
                                fullWidth
                                variant="outlined"
                                autoComplete="nope"
                                label="Notes"
                                name="notes"
                                multiline
                                minRows={3}
                                inputProps={{
                                  style: {
                                    resize: 'vertical'
                                  }
                                }}
                                required
                                disabled={!canEdit || isModal}
                                value={values.notes}
                                onBlur={handleBlur}
                                onChange={e => setFieldValue('notes', e.target.value)}
                                error={Boolean(touched.notes && errors.notes)}
                                helperText={touched.notes && errors.notes}
                              />
                            );
                          }}
                        </Field>
                      </Grid>
                      {(values.orderType === 6 || values.orderType === 10) && (
                        <Grid item xs={12}>
                          <FieldArray name="chargeVendors">
                            {({ push, remove }) => {
                              return (
                                <>
                                  <Grid container spacing={1} alignItems="center">
                                    {values.chargeVendors.map((vendor, index) => {
                                      return (
                                        <Fragment key={`${index}`}>
                                          <Grid item xs={12} sm={5}>
                                            <FormControl
                                              required
                                              fullWidth
                                              variant="outlined"
                                              size="small"
                                            >
                                              <InputLabel
                                                id={`chargeVendors`}
                                                className={classes.outlinedLabel}
                                              >
                                                Charge Vendor
                                              </InputLabel>
                                              <Select
                                                name={`chargeVendors`}
                                                labelId={`chargeVendors`}
                                                id={`chargeVendors`}
                                                size="small"
                                                value={vendor.vendorId}
                                                fullWidth
                                                onBlur={handleBlur}
                                                disabled={isLoadingVendors || !canEdit || isModal}
                                                onChange={e =>
                                                  setFieldValue(
                                                    `chargeVendors.${index}.vendorId`,
                                                    e.target.value
                                                  )
                                                }
                                              >
                                                {availableVendors &&
                                                  availableVendors.map((vendor, index) => {
                                                    return (
                                                      <MenuItem key={`${index}`} value={vendor.id}>
                                                        {vendor.name}
                                                      </MenuItem>
                                                    );
                                                  })}
                                              </Select>
                                            </FormControl>
                                          </Grid>
                                          <Grid item xs={12} sm={5}>
                                            <TextField
                                              fullWidth
                                              variant="outlined"
                                              autoComplete="nope"
                                              label="Amount"
                                              name="amount"
                                              size="small"
                                              disabled={!canEdit || isModal}
                                              value={vendor.amount}
                                              onBlur={e => {
                                                // Need to strip out all non numeric values from formatted value
                                                const value = e.target.value
                                                  .replace('$', '')
                                                  .replace(',', '');
                                                // make sure it is number only before we update state
                                                if (value === '' || decimalRegExp.test(value)) {
                                                  // setActualPreferredLenderClosingCostContribution(Number(value));
                                                }
                                                setFieldValue(
                                                  `chargeVendors.${index}.amount`,
                                                  formatMoney(e.target.value, 2)
                                                );
                                              }}
                                              onChange={e =>
                                                setFieldValue(
                                                  `chargeVendors.${index}.amount`,
                                                  e.target.value
                                                )
                                              }
                                            />
                                          </Grid>
                                          <Grid
                                            item
                                            xs={12}
                                            sm={2}
                                            className={classes.removeVendorButtonWrapper}
                                          >
                                            <Button
                                              id="remove-vendor"
                                              className={classes.removeVendorButton}
                                              startIcon={<Close />}
                                              variant="text"
                                              onClick={() => {
                                                remove(index);
                                              }}
                                            >
                                              Remove
                                            </Button>
                                          </Grid>
                                        </Fragment>
                                      );
                                    })}
                                  </Grid>
                                  <Button
                                    id="add-vendor"
                                    color="primary"
                                    variant="text"
                                    className={classes.addButton}
                                    startIcon={<Add />}
                                    onClick={() =>
                                      push({
                                        vendorId: '',
                                        amount: ''
                                      })
                                    }
                                  >
                                    Add Charge Vendor
                                  </Button>
                                </>
                              );
                            }}
                          </FieldArray>
                        </Grid>
                      )}
                      {canEdit && !isModal && !isLoadingReturnParts && (
                        <Grid item xs={12} className={classes.findPartWrapper}>
                          <SMButton
                            type="button"
                            color="secondary"
                            variant="contained"
                            startIcon={<Search />}
                            onClick={() => setShowFindPartsModal(true)}
                          >
                            Find Part
                          </SMButton>
                        </Grid>
                      )}
                      <FindPartsModal
                        values={values}
                        open={showFindPartsModal}
                        onClose={() => setShowFindPartsModal(false)}
                        cartParts={parts}
                        returnParts={returnParts}
                        setReturnParts={setReturnParts}
                        isReturnOrder={isReturnPOOrderType}
                        handlePartSelection={(part: IPart & IReturnPOPart) => {
                          const foundPartIndex = parts.findIndex(p => p.partId === part.id);
                          const foundPart = parts.find(p => p.partId === part.id);
                          // the part already exists in cart so then just add them together
                          // ignore for "Return PO" order type orders
                          if (foundPartIndex >= 0 && !isReturnPOOrderType) {
                            // make a copy of the parts so we don't change the original
                            const copy = JSON.parse(JSON.stringify(parts));
                            copy[foundPartIndex] = {
                              ...part,
                              // add quantity together to get the total
                              quantity: (Number(part.quantity) +
                                Number(foundPart.quantity)) as unknown as string,
                              hasPartPermission: true,
                              vendorId: part?.vendorId ?? null,
                              pricePerUnit: part?.pricePerUnit ? part?.pricePerUnit : part?.price
                            };
                            setParts(copy);
                          } else {
                            setParts([
                              ...parts,
                              {
                                ...part,
                                partId: part.id,
                                partName: part.partName,
                                quantity: Number(part.quantity),
                                hasPartPermission: true,
                                vendorId: part?.vendorId ?? null,
                                undefinedPartDescriptionId: part?.undefinedPartDescriptionId ?? null
                              }
                            ]);
                          }
                          enqueueSnackbar('Part added to order!', {
                            variant: 'success'
                          });
                          setFieldTouched('orderType', false);
                        }}
                      />
                      <Grid item>
                        <Typography variant="h6" className={classes.partsTitle}>
                          Cart
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Table
                          columns={columns}
                          data={parts}
                          hidePagination
                          noResultsText="No Parts"
                          ResponsiveComponent={isMobile ? MobileParts : undefined}
                          useTableProps={{
                            updateMyData: updateMyPartsData
                          }}
                          mobileProps={{
                            isModal,
                            isEdit: true,
                            showPrice: true,
                            isLoadingUndefinedParts,
                            isReturnPOOrderType,
                            undefinedParts,
                            handleDelete: (rowIndex: number) => {
                              const result = window.confirm(
                                'Are you sure you want to remove this part from your order?'
                              );
                              if (result) {
                                setParts(parts.filter((_, index) => index !== rowIndex));
                              }
                            },
                            handleQtyChange: (rowIndex: number, qty: string) => {
                              const copy = JSON.parse(JSON.stringify(parts));
                              copy[rowIndex] = {
                                ...copy[rowIndex],
                                quantity: Number(qty)
                              };
                              setParts(copy);
                            },
                            handleVendorChange: (
                              rowIndex: number,
                              vendorId: number,
                              pricePerUnit: number
                            ) => {
                              const copy = JSON.parse(JSON.stringify(parts));
                              copy[rowIndex] = {
                                ...copy[rowIndex],
                                vendorId,
                                pricePerUnit
                              };
                              setParts(copy);
                            },
                            handlePriceChange: (
                              rowIndex: number,
                              pricePerUnit: number | undefined
                            ) => {
                              const copy = JSON.parse(JSON.stringify(parts));
                              if (pricePerUnit) {
                                copy[rowIndex] = {
                                  ...copy[rowIndex],
                                  pricePerUnit: pricePerUnit
                                };
                              } else {
                                delete copy[rowIndex].pricePerUnit;
                              }
                              setParts(copy);
                            },
                            handlePartNameChange: (
                              rowIndex: number,
                              undefinedPartDescriptionId: number
                            ) => {
                              const copy = JSON.parse(JSON.stringify(parts));
                              copy[rowIndex] = {
                                ...copy[rowIndex],
                                undefinedPartDescriptionId
                              };
                              setParts(copy);
                            },
                            handleUndefinedPartsCallback: callback => {
                              fetchUndefinedPartOptions(callback);
                            }
                          }}
                        />
                      </Grid>
                      {(values.status !==
                        (currentOrder &&
                          currentOrder.currentStatus &&
                          currentOrder.currentStatus.status) ||
                        currentOrder.currentStatus.comment) && (
                        <Grid item xs={12} className={classes.marginTop}>
                          <Field name={`comments`}>
                            {({ field }) => {
                              return (
                                <TextField
                                  fullWidth
                                  required={
                                    values.status !==
                                      (currentOrder &&
                                        currentOrder.currentStatus &&
                                        currentOrder.currentStatus.status) &&
                                    values.status === 'Remediating'
                                  }
                                  variant="outlined"
                                  autoComplete="nope"
                                  label="Comments"
                                  name="comments"
                                  multiline
                                  minRows={3}
                                  inputProps={{
                                    style: {
                                      resize: 'vertical'
                                    }
                                  }}
                                  disabled={!canEdit || isModal}
                                  value={values.comments}
                                  onBlur={handleBlur}
                                  onChange={e => setFieldValue('comments', e.target.value)}
                                  error={Boolean(touched.comments && errors.comments)}
                                  helperText={touched.comments && errors.comments}
                                />
                              );
                            }}
                          </Field>
                        </Grid>
                      )}
                      {!isModal && (
                        <Grid item xs={12}>
                          <PlaceOrderImages
                            setFieldValue={setFieldValue}
                            photos={values.photos}
                            photoGuids={values.photoGuids}
                            isRequired={isReturnPOOrderType}
                            setSelectedImage={setSelectedImage}
                          />
                        </Grid>
                      )}
                      <OrderHistory currentOrder={currentOrder} />
                      {!isModal && canEdit && (
                        <Grid item xs={12} className={classes.buttonWrapper}>
                          <ButtonGroup>
                            <Button
                              id="save"
                              disabled={
                                (!dirty && !partsChanged) ||
                                !isValid ||
                                isModal ||
                                checkParts() ||
                                checkVendors() ||
                                checkImages()
                              }
                              type="submit"
                              color="primary"
                              variant="contained"
                              startIcon={<Save />}
                            >
                              Save
                            </Button>
                            <SMButton
                              id="cancel"
                              disabled={(!dirty && !partsChanged) || isModal}
                              type="button"
                              variant="contained"
                              startIcon={<Close />}
                              color="inherit"
                              onClick={() => history.push('/production/orders/order-management')}
                            >
                              Cancel
                            </SMButton>
                          </ButtonGroup>
                        </Grid>
                      )}
                    </Grid>
                  </>
                )}
              </OrderManagementPage>
            </Form>
          );
        }}
      </Formik>

      <StatusHistoryModal
        open={isShowingStatusHistory}
        onClose={() => showStatusHistory(false)}
        statuses={currentOrder ? currentOrder.statuses : []}
      />
      <ImageModal
        isOpen={!!selectedImage}
        onClose={() => setSelectedImage('')}
        source={selectedImage}
      />
    </div>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  marginTop: {
    marginTop: theme.spacing(0.5)
  },
  outlinedLabel: {
    backgroundColor: theme.palette.common.white,
    paddingLeft: 2,
    paddingRight: 2
  },
  partsTitle: {
    marginTop: theme.spacing(1)
  },
  deleteButton: {
    color: theme.palette.error.main
  },
  Vendor: {
    textAlign: 'center'
  },
  buttonWrapper: {
    textAlign: 'right'
  },
  pageTitleWrapper: {
    '@media (min-width: 768px)': {
      flex: 1
    }
  },
  titleWrapper: {
    marginBottom: theme.spacing(0.5)
  },
  statusRed: {
    color: theme.palette.error.main
  },
  infoWrapper: {
    '@media (min-width: 768px)': {
      textAlign: 'right'
    }
  },
  addCartWrapper: {
    [theme.breakpoints.up('md')]: {
      marginTop: 8
    }
  },
  findPartWrapper: {
    [theme.breakpoints.up('md')]: {
      marginTop: 8
    }
  },
  addButton: {
    marginTop: theme.spacing(1)
  },
  removeVendorButton: {
    color: theme.palette.error.main
  },
  removeVendorButtonWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '-8px'
  },
  undefinedPart: {
    color: theme.palette.error.main
  }
}));
